import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineMagnifyingGlass,
  HiOutlineAdjustmentsHorizontal,
  HiOutlineArrowPath,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineClipboardDocumentList,
  HiOutlineXMark,
  HiOutlinePlus,
  HiOutlineLightBulb,
  HiOutlinePrinter,
  HiOutlineTableCells,
} from "react-icons/hi2";
import {
  authService,
  kaizenService,
  masterDataService,
  machineService,
  productService,
} from "../services";
import plantService from '../services/plantService';
import { usePermissions } from "../hooks/usePermissions";
import { toast } from "react-toastify";
import SharedModal from "../components/common/SharedModal";
import PageLoading from "../components/common/PageLoading";
import "./Kaizen.css";

const Kaizen = () => {
  const navigate = useNavigate();
  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission("Kaizen.View");
  const canCreate = hasPermission("Kaizen.Create");
  const canEdit = hasPermission("Kaizen.Edit");
  const canDelete = hasPermission("Kaizen.Delete");
  const canApprove = hasPermission("Kaizen.Approve");

  const [ideas, setIdeas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [machines, setMachines] = useState([]);
  const [products, setProducts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  // Check for cached data immediately to set initial loading state
  const hasCachedData =
    sessionStorage.getItem("kaizen_ideas_v2") &&
    sessionStorage.getItem("kaizen_categories") &&
    sessionStorage.getItem("machines_data") &&
    sessionStorage.getItem("products_data") &&
    sessionStorage.getItem("kaizen_statuses");
  const [loading, setLoading] = useState(!hasCachedData);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    plantId: "",
    statusId: "",
    categoryId: "",
    machineId: "",
  });
  const [plants, setPlants] = useState([]);

  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Load cached data immediately on mount (before feature check)
  useEffect(() => {
    if (tenantId) {
      const cachedIdeas = sessionStorage.getItem("kaizen_ideas_v2");
      const cachedCategories = sessionStorage.getItem("kaizen_categories");
      const cachedMachines = sessionStorage.getItem("machines_data");
      const cachedProducts = sessionStorage.getItem("products_data");
      const cachedStatuses = sessionStorage.getItem("kaizen_statuses");

      if (cachedIdeas) {
        try {
          setIdeas(JSON.parse(cachedIdeas));
        } catch (e) {
          console.error("Error parsing cached ideas:", e);
        }
      }
      if (cachedCategories) {
        try {
          setCategories(JSON.parse(cachedCategories));
        } catch (e) {
          console.error("Error parsing cached categories:", e);
        }
      }
      if (cachedMachines) {
        try {
          setMachines(JSON.parse(cachedMachines));
        } catch (e) {
          console.error("Error parsing cached machines:", e);
        }
      }
      if (cachedProducts) {
        try {
          setProducts(JSON.parse(cachedProducts));
        } catch (e) {
          console.error("Error parsing cached products:", e);
        }
      }
      if (cachedStatuses) {
        try {
          setStatuses(JSON.parse(cachedStatuses));
        } catch (e) {
          console.error("Error parsing cached statuses:", e);
        }
      }

      // If we have all cached data, hide loading immediately
      if (
        cachedIdeas &&
        cachedCategories &&
        cachedMachines &&
        cachedProducts &&
        cachedStatuses
      ) {
        setLoading(false);
      }
    }
  }, [tenantId]);

  useEffect(() => {
    if (!canView) {
      setError("You do not have permission to view Kaizen ideas");
      setLoading(false);
      return;
    }
    if (tenantId) {
      loadInitialData();
    }
  }, [canView, tenantId]);

  const loadInitialData = async () => {
    try {
      // Only show loading if we don't have cached data
      const hasCachedData = sessionStorage.getItem("kaizen_ideas_v2");
      if (!hasCachedData) {
        setLoading(true);
      }
      setError("");

      const [
        ideasData,
        categoriesData,
        machinesData,
        productsData,
        statusesData,
        plantsData,
      ] = await Promise.all([
        kaizenService.getAllKaizenIdeas(),
        masterDataService.getCategories?.(tenantId) || Promise.resolve([]),
        machineService.getAllMachines(),
        productService.getAllProducts(),
        masterDataService.getStatuses?.(tenantId) || Promise.resolve([]),
        plantService.getAllPlants(),
      ]);

      setPlants(plantsData || []);

      // Auto-select plant
      if (plantsData?.length === 1) {
        setFilters(prev => ({ ...prev, plantId: plantsData[0].plantId.toString() }));
      }

      setIdeas(ideasData);

      // Filter categories for Kaizen type
      const kaizenCategories = categoriesData.filter(
        (c) => c.categoryType === "Kaizen",
      );
      setCategories(kaizenCategories);

      setMachines(machinesData);
      setProducts(productsData);

      // Filter statuses for Kaizen type
      const kaizenStatuses = statusesData.filter(
        (s) => s.statusType === "Kaizen",
      );
      setStatuses(kaizenStatuses);

      // Cache the filtered data
      sessionStorage.setItem("kaizen_ideas_v2", JSON.stringify(ideasData));
      sessionStorage.setItem(
        "kaizen_categories",
        JSON.stringify(kaizenCategories),
      );
      sessionStorage.setItem("machines_data", JSON.stringify(machinesData));
      sessionStorage.setItem("products_data", JSON.stringify(productsData));
      sessionStorage.setItem("kaizen_statuses", JSON.stringify(kaizenStatuses));
    } catch (err) {
      console.error("Error loading Kaizen ideas:", err);
      setError("Failed to load Kaizen ideas. Please try again.");
      toast.error("Failed to load Kaizen ideas.", { closeButton: true });
    } finally {
      setLoading(false);
    }
  };

  const fetchIdeasWithFilters = async () => {
    try {
      setRefreshing(true);
      setError("");

      const data = await kaizenService.getAllKaizenIdeas(
        filters.statusId ? parseInt(filters.statusId) : null,
        null, // submitterId
      );
      setIdeas(data);
    } catch (err) {
      console.error("Error fetching Kaizen ideas:", err);
      setError("Failed to fetch Kaizen ideas. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    fetchIdeasWithFilters();
    setShowFilterDrawer(false);
  };

  const clearFilters = () => {
    setFilters({
      plantId: "",
      statusId: "",
      categoryId: "",
      machineId: "",
    });
    setSearchTerm("");
    fetchIdeasWithFilters();
  };

  const getCategoryName = (categoryId) => {
    if (!categoryId) return "-";
    const category = categories.find((c) => c.categoryId === categoryId);
    return category ? category.categoryName : `Category ${categoryId}`;
  };

  const getMachineName = (machineId) => {
    if (!machineId) return "-";
    const machine = machines.find((m) => m.machineId === machineId);
    return machine
      ? `${machine.machineName} (${machine.machineCode})`
      : `Machine ${machineId}`;
  };

  const getProductName = (productId) => {
    if (!productId) return "-";
    const product = products.find((p) => p.productId === productId);
    return product
      ? `${product.productName} (${product.productCode})`
      : `Product ${productId}`;
  };

  const getStatusName = (statusId) => {
    if (!statusId) return "-";
    const status = statuses.find((s) => s.statusId === statusId);
    return status ? status.statusName : null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const filteredIdeas = useMemo(() => {
    return ideas.filter((idea) => {
      const matchesSearch =
        !searchTerm ||
        (idea.title &&
          idea.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (idea.problemStatement &&
          idea.problemStatement
            .toLowerCase()
            .includes(searchTerm.toLowerCase()));

      const matchesStatus =
        !filters.statusId || idea.statusId?.toString() === filters.statusId;
      const matchesCategory =
        !filters.categoryId ||
        idea.categoryId?.toString() === filters.categoryId;
      const matchesPlant = 
        !filters.plantId || idea.plantId?.toString() === filters.plantId;
      const matchesMachine =
        !filters.machineId || idea.machineId?.toString() === filters.machineId;

      return (
        matchesSearch && matchesStatus && matchesCategory && matchesPlant && matchesMachine
      );
    });
  }, [ideas, filters, searchTerm, categories, machines, products, statuses]);

  const summaryStats = useMemo(() => {
    if (filteredIdeas.length === 0) {
      return {
        totalIdeas: 0,
        totalSavings: 0,
        avgSavings: 0,
      };
    }

    const totalSavings = filteredIdeas.reduce(
      (sum, idea) => sum + (parseFloat(idea.estimatedSavings) || 0),
      0,
    );

    return {
      totalIdeas: filteredIdeas.length,
      totalSavings: totalSavings,
      avgSavings:
        filteredIdeas.length > 0
          ? (totalSavings / filteredIdeas.length).toFixed(2)
          : 0,
    };
  }, [filteredIdeas]);

  const handleDelete = async (idea) => {
    if (!canDelete) {
      setError("You do not have permission to delete Kaizen ideas");
      setDeleteConfirm(null);
      return;
    }

    try {
      const toastId = toast.loading("Deleting Kaizen idea...");
      await kaizenService.deleteKaizenIdea(idea.ideaId);
      await fetchIdeasWithFilters();
      setDeleteConfirm(null);
      toast.update(toastId, { render: "Kaizen idea deleted successfully!", type: "success", isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      console.error("Error deleting Kaizen idea:", err);
      setError("Failed to delete Kaizen idea. Please try again.");
      toast.error("Failed to delete Kaizen idea.", { closeButton: true });
    }
  };

  if (loading) {
    return (
      <div className="pagecontainer">
        <PageLoading message="Loading Kaizen ideas..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer">
      <div className="page-header">
        <div className="header-content">
          <h1>Kaizen Ideas</h1>
          <p>Manage continuous improvement ideas and track their progress</p>
        </div>
        {canView && (
          <div className="header-actions">
            {canCreate && (
              <button
                className="btn-primary"
                onClick={() => navigate("/home/kaizen-entry")}
              >
                <HiOutlinePlus />
                New Idea
              </button>
            )}

            <button
              className="btn-secondary"
              onClick={clearFilters}
              disabled={refreshing}
            >
              <HiOutlineArrowPath className={refreshing ? "spinning" : ""} />
              Refresh
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {canView && (
        <div className="kaizen-summary-cards">
          <div className="summary-card">
            <span className="summary-label kpi-label">Total Ideas</span>
            <strong className="summary-value kpi-value empty">{summaryStats.totalIdeas}</strong>
            <div>
              <svg class="kpi-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a7 7 0 017 7c0 2.5-1.3 4.7-3.3 6l-.7.5V18H9v-2.5l-.7-.5A7 7 0 0112 2z" /><rect x="9" y="19" width="6" height="2" rx="1" /><rect x="10" y="21" width="4" height="1" rx=".5" /></svg>
            </div>
          </div>
          <div className="summary-card">
            <span className="summary-label kpi-label">Total Estimated Savings</span>
            <strong className="summary-value kpi-value empty">
              {formatCurrency(summaryStats.totalSavings)}
            </strong>
            <svg class="kpi-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm.5 14.5v1h-1v-1c-1.38-.25-2.5-1.26-2.5-2.5h1.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5c0-.55-.28-1.04-.75-1.32L10 11.25C9.23 10.79 9 10.04 9 9.5c0-1.24 1.12-2.25 2.5-2.5V6h1v1c1.38.25 2.5 1.26 2.5 2.5h-1.5c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5c0 .55.28 1.04.75 1.32L14 12.75c.77.46 1 1.21 1 1.75 0 1.24-1.12 2.25-2.5 2.5z" /></svg>
          </div>

          <div className="summary-card highlight">
            <span className="summary-label kpi-label">Avg Savings per Idea</span>
            <strong className="summary-value kpi-value empty">
              {formatCurrency(summaryStats.avgSavings)}
            </strong>
            <svg class="kpi-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" /></svg>
          </div>
        </div>
      )}

      <div className="table-container pad10 kizantable">
        <div className="table-header stacked">
          <div className="left-group">
            <div className="search-box">
              <HiOutlineMagnifyingGlass className="search-icon" />
              <input
                type="text"
                placeholder="Search title, problem statement..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => setSearchTerm("")}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "0.25rem",
                    display: "flex",
                    alignItems: "center",
                    color: "var(--text-secondary)",
                    marginLeft: "0.5rem",
                  }}
                  title="Clear search"
                >
                  <HiOutlineXMark size={18} />
                </button>
              )}
            </div>

            <button
              className="filter-toggle"
              type="button"
              onClick={() => setShowFilterDrawer(true)}
            >
              <HiOutlineAdjustmentsHorizontal />
              <span>Filters</span>
            </button>
          </div>
        </div>

        {filteredIdeas.length === 0 ? (
          canView && (
            <div className="empty-state">
              <HiOutlineLightBulb className="empty-icon" />
              <h3>No Kaizen ideas found</h3>
              <p>
                {searchTerm ||
                  filters.statusId ||
                  filters.categoryId ||
                  filters.machineId
                  ? "Try adjusting your filters or search terms"
                  : "Start by creating your first Kaizen idea"}
              </p>
              {canCreate && (
                <button
                  className="btn-primary btn"
                  onClick={() => navigate("/home/kaizen-entry")}
                >
                  <HiOutlinePlus />
                  Create First Idea
                </button>
              )}
            </div>
          )
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: "150px", textAlign: "center" }}>Actions</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Machine / Product</th>
                    <th>Problem Statement</th>
                    <th>Reference</th>
                    <th>Estimated Savings</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIdeas.map((idea) => (
                    <tr key={idea.ideaId}>
                      <td>
                        <div className="action-buttons">
                          {canEdit && (
                            <button
                              className="btn-icon-edit"
                              onClick={() =>
                                navigate(
                                  `/home/kaizen-entry/${idea.ideaId}`,
                                )
                              }
                              title="Edit idea"
                            >
                              <HiOutlinePencil />
                            </button>
                          )}
                          <button
                            className="btn-icon-edit"
                            onClick={() =>
                              navigate(
                                `/home/kaizen-entry/${idea.ideaId}`,
                                {
                                  state: { autoPrint: true },
                                },
                              )
                            }
                            title="Print Kaizen"
                          >
                            <HiOutlinePrinter />
                          </button>
                          <button
                            className="btn-icon-edit"
                            onClick={() =>
                              navigate(
                                `/home/kaizen-entry/${idea.ideaId}`,
                                {
                                  state: { autoExport: true },
                                },
                              )
                            }
                            title="Export to Excel"
                          >
                            <HiOutlineTableCells />
                          </button>
                          {canDelete && (
                            <button
                              className="btn-icon-delete"
                              onClick={() => setDeleteConfirm(idea)}
                              title="Delete idea"
                            >
                              <HiOutlineTrash />
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="cell-group">
                          <span className="cell-primary">{idea.title}</span>
                        </div>
                      </td>
                      <td>{getCategoryName(idea.categoryId)}</td>
                      <td>
                        <div className="cell-group">
                          {idea.machineId && (
                            <span className="cell-secondary">
                              Machine: {getMachineName(idea.machineId)}
                            </span>
                          )}
                          {idea.productId && (
                            <span className="cell-secondary">
                              Product: {getProductName(idea.productId)}
                            </span>
                          )}
                          {!idea.machineId && !idea.productId && <span>-</span>}
                        </div>
                      </td>
                      <td>
                        <div className="cell-group">
                          <span className="cell-primary">
                            {idea.problemStatement
                              ? idea.problemStatement.length > 100
                                ? `${idea.problemStatement.substring(0, 100)}...`
                                : idea.problemStatement
                              : "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        {idea.referenceNumber ? (
                          <span
                            className={`reference-badge ${idea.moduleName === "FiveWhys" ? "clickable" : ""}`}
                            onClick={(e) => {
                              if (idea.moduleName === "FiveWhys") {
                                e.stopPropagation();
                                navigate(
                                  `/home/rca/five-whys?rcaId=${idea.referenceNumber}`,
                                );
                              }
                            }}
                            title={
                              idea.moduleName === "FiveWhys"
                                ? "View Five Whys Analysis"
                                : ""
                            }
                          >
                            #{idea.referenceNumber}{" "}
                            {idea.moduleName ? `- ${idea.moduleName}` : ""}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <span className="savings-badge">
                          {formatCurrency(idea.estimatedSavings)}
                        </span>
                      </td>
                      <td>{getStatusName(idea.statusId)}</td>
                      <td>{formatDate(idea.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="kaizen-ideas-cards">
              {filteredIdeas.map((idea) => (
                <div key={idea.ideaId} className="kaizen-idea-card">
                  <div className="card-header">
                    <div className="card-header-left">
                      <div className="card-value">{idea.title}</div>
                      <div className="status-badge">
                        {getStatusName(idea.statusId)}
                      </div>
                    </div>
                    <div className="card-header-right">
                      {canEdit && (
                        <button
                          className="btn-icon-edit"
                          onClick={() =>
                            navigate(`/home/kaizen-entry/${idea.ideaId}`)
                          }
                          title="Edit idea"
                        >
                          <HiOutlinePencil />
                        </button>
                      )}
                      <button
                        className="btn-icon"
                        onClick={() =>
                          navigate(`/home/kaizen-entry/${idea.ideaId}`, {
                            state: { autoPrint: true },
                          })
                        }
                        title="Print Kaizen"
                      >
                        <HiOutlinePrinter />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() =>
                          navigate(`/home/kaizen-entry/${idea.ideaId}`, {
                            state: { autoExport: true },
                          })
                        }
                        title="Export to Excel"
                      >
                        <HiOutlineTableCells />
                      </button>
                      {canDelete && (
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(idea)}
                          title="Delete idea"
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="card-row">
                    <span className="card-label">Category</span>
                    <span className="card-value">
                      {getCategoryName(idea.categoryId)}
                    </span>
                  </div>

                  {(idea.machineId || idea.productId) && (
                    <div className="card-row">
                      <span className="card-label">Machine / Product</span>
                      <div>
                        {idea.machineId && (
                          <div className="card-value">
                            {getMachineName(idea.machineId)}
                          </div>
                        )}
                        {idea.productId && (
                          <div className="card-secondary">
                            {getProductName(idea.productId)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {idea.problemStatement && (
                    <div className="card-row">
                      <span className="card-label">Problem</span>
                      <span className="card-value">
                        {idea.problemStatement.length > 150
                          ? `${idea.problemStatement.substring(0, 150)}...`
                          : idea.problemStatement}
                      </span>
                    </div>
                  )}

                  {idea.estimatedSavings && (
                    <div className="card-row">
                      <span className="card-label">Estimated Savings</span>
                      <span className="card-value savings">
                        {formatCurrency(idea.estimatedSavings)}
                      </span>
                    </div>
                  )}

                  <div className="card-row">
                    <span className="card-label">Created</span>
                    <span className="card-value">
                      {formatDate(idea.createdAt)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Filter Drawer */}
      {showFilterDrawer && (
        <div
          className="filter-drawer-overlay"
          onClick={() => setShowFilterDrawer(false)}
        >
          <div className="filter-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="filter-drawer-header">
              <h3>Filters</h3>
              <button
                className="btn-close"
                onClick={() => setShowFilterDrawer(false)}
              >
                <HiOutlineXMark />
              </button>
            </div>
            <div className="filter-drawer-content">
              <div className="form-group">
                <label>Plant</label>
                <select
                  name="plantId"
                  value={filters.plantId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Plants</option>
                  {plants.map((plant) => (
                    <option key={plant.plantId} value={plant.plantId}>
                      {plant.plantName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  name="statusId"
                  value={filters.statusId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Statuses</option>
                  {statuses.map((status) => (
                    <option key={status.statusId} value={status.statusId}>
                      {status.statusName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  name="categoryId"
                  value={filters.categoryId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option
                      key={category.categoryId}
                      value={category.categoryId}
                    >
                      {category.categoryName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Machine</label>
                <select
                  name="machineId"
                  value={filters.machineId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Machines</option>
                  {machines.map((machine) => (
                    <option key={machine.machineId} value={machine.machineId}>
                      {machine.machineName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="filter-drawer-footer">
              <button className="btn-secondary btn" onClick={clearFilters}>
                Clear
              </button>
              <button className="btn-primary btn" onClick={applyFilters}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Kaizen Idea"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger btn"
              onClick={() => handleDelete(deleteConfirm)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete "{deleteConfirm?.title}"? This
            action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default Kaizen;
